//
// Copyright 2025 DXOS.org
//

import * as GraphModel from '@dxos/graph/GraphModel';
import type * as SelectionModel from '@dxos/graph/SelectionModel';

import { type SVGContext } from '../../hooks/index.ts';
import { type GraphLayout, type GraphLayoutNode, emptyGraph } from '../types.ts';
import { Projector, type ProjectorOptions } from './projector.ts';

export type GraphProjectorOptions = ProjectorOptions & {};

/**
 * Base class for graph projectors.
 */
export abstract class GraphProjector<NodeData = any, Options extends GraphProjectorOptions = any> extends Projector<
  GraphModel.AnyData,
  GraphLayout<NodeData>,
  Options
> {
  private readonly _layout: GraphLayout<NodeData>;

  // TODO(burdon): Change to object props.
  constructor(
    context: SVGContext,
    options?: Options,
    private readonly _selection?: SelectionModel.SelectionModel,
    layout?: GraphLayout<NodeData>,
  ) {
    super(context, options);
    this._layout = {
      id: (Math.random() * 10).toString().slice(8),
      graph: {
        nodes: [...(layout?.graph.nodes ?? [])],
        edges: [...(layout?.graph.edges ?? [])],
      },
    };
  }

  get layout() {
    return this._layout;
  }

  get selection() {
    return this._selection;
  }

  numChildren(node: GraphLayoutNode) {
    return this._layout.graph.edges.filter((edge) => edge.source.id === this.options.idAccessor(node)).length;
  }

  reset() {
    this._layout.graph.nodes = [];
    this._layout.graph.edges = [];
    this.emitUpdate();
  }

  /**
   * Merge external data with internal representation (e.g., so force properties like position are preserved).
   * @param data
   */
  protected mergeData(data: GraphModel.AnyData = emptyGraph) {
    // Merge nodes.
    const nodes: GraphLayoutNode[] = data.nodes.map((node) => {
      let current: GraphLayoutNode = this._layout.graph.nodes.find((n) => n.id === this.options.idAccessor(node));
      if (!current) {
        current = {
          id: this.options.idAccessor(node),
        };
      }

      current.data = node;
      // `hidden` is a transient flag set by the cluster projector to fade
      // collapsed leaves. Reset it on every merge so that (a) switching
      // away from cluster to another projector — which inherits the
      // previous layout via `prev` — doesn't carry forward stale hidden
      // flags, and (b) the cluster projector itself re-applies the flag
      // each `doClusterLayout` pass.
      current.hidden = false;
      return current;
    });

    // Replace edges. Preserve `type` so projectors can classify edges by their source
    // semantics (e.g. the plexus projector groups by relation vs ref); projectors that
    // synthesize their own edges (bundle, cluster) overwrite this downstream.
    const edges = data.edges
      .map((edge) => ({
        id: edge.id,
        type: (edge as { type?: string }).type,
        source: nodes.find((n) => n.id === edge.source),
        target: nodes.find((n) => n.id === edge.target),
        data: edge.data,
      }))
      .filter((edge) => edge.source && edge.target);

    Object.assign(this._layout, {
      graph: {
        nodes,
        edges,
      },
    });
  }

  override async onClear() {
    this.reset();
  }

  /**
   * Optional projector-owned click intercept. The Graph component invokes this BEFORE
   * forwarding to the consumer's `onSelect`, so projectors can implement built-in
   * interactions (e.g. cluster collapse) without each consumer wiring them. Returning
   * `true` indicates the click was handled — consumer's `onSelect` is suppressed.
   * Default no-op returns `false` so consumer handlers still run.
   */
  handleNodeClick(_node: GraphLayoutNode<NodeData>, _event: MouseEvent): boolean {
    return false;
  }

  abstract findNode(x: number, y: number, radius: number): GraphLayoutNode<NodeData> | undefined;
}
